import React from "react";
import { useSelector } from "react-redux";
import cx from "classnames";
import { noop, isEmpty } from "lodash";
import stylesV2 from "./DoctorTimeSlotV2.module.scss";
import stylesV3 from "./DoctorTimeSlotV3.module.scss";
import { selectUpcomingTimeslotsForDoctorByDate } from "features/scheduling/timeSlotsSlice";
import { toTel } from "features/scheduling/utils/toTel";
import { Slot } from "types/slot";
import { Office } from "types/office";
import { RootState } from "app/store";
import { SlotButton } from "features/scheduling/components/SlotButtons";
import {
  Button,
  ButtonV2,
  ButtonLinkExternal,
  ButtonRole,
} from "components/Button";
import { ButtonSkeleton } from "components/Button/ButtonSkeleton";
import morningIcon from "images/morningIcon.svg";
import afternoonIcon from "images/afternoonIcon.svg";
import eveningIcon from "images/eveningIcon.svg";
import { parseStartTime } from "features/appointmentTime";
import { useSchedulingABVariant } from "../utils/useSchedulingABVariant";

interface DoctorTimeSlotV2Props {
  doctorId: string;
  searchDate: string;
  selectedOffice: Office;
  onSelect?: (x: Slot) => void;
  forDate?: string;
  availability: any;
  onSelection?: (type: string, value: Slot) => void;
  onSelectNextAvail?: (date: string) => void;
  selectedSlot: undefined | Slot;
  isMobileView?: boolean;
  onNextMobile?: () => void;
  useClassicStartLayout?: boolean;
}
export const DoctorTimeSlotV2: React.FC<DoctorTimeSlotV2Props> = ({
  searchDate,
  doctorId,
  selectedOffice,
  availability,
  onSelection,
  onSelectNextAvail,
  selectedSlot,
  isMobileView,
  onNextMobile,
  useClassicStartLayout = false,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles =
    isV3 && !useClassicStartLayout ? stylesV3 : stylesV2;
  const nextButtonRef = React.useRef<HTMLDivElement | null>(null);
  const hasMountedRef = React.useRef(false);
  const timeSlotsForDoctor = useSelector((state: RootState) =>
    selectUpcomingTimeslotsForDoctorByDate(state, doctorId)
  );

  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (
      !isV3 ||
      useClassicStartLayout ||
      !isMobileView ||
      selectedSlot?.doctorId !== doctorId ||
      !nextButtonRef.current
    ) {
      return;
    }

    window.requestAnimationFrame(() => {
      nextButtonRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    });
  }, [doctorId, isMobileView, isV3, selectedSlot, useClassicStartLayout]);

  if (availability === "loading") {
    return (
      <>
        {["1", "2", "3", "4", "5", "6", "7", "8"].map((slotIndex) => (
          <div key={slotIndex} className={styles.slots}>
            <ButtonSkeleton />
          </div>
        ))}
      </>
    );
  }
  if (
    (isEmpty(timeSlotsForDoctor) || isEmpty(timeSlotsForDoctor[searchDate])) &&
    availability === null &&
    isMobileView
  ) {
    return (
      <>
        <div className={cx(styles.noAvailabilityContainer, styles.officeCall)}>
          <ButtonLinkExternal
            href={toTel(selectedOffice.formattedPhone)}
            text="Call The Office"
            role={ButtonRole.Ghost}
          />
        </div>
      </>
    );
  }
  if (
    (isEmpty(timeSlotsForDoctor) && availability) ||
    searchDate !== availability
  ) {
    return (
      <>
        <div className={styles.noAvailabilityContainer}>
          {!isMobileView && (
            <div className={styles.divLabel}>
              This doctor is not available on the selected date. Would you like
              to check <br />
              their next available slot?
            </div>
          )}
          {isMobileView && (
            <div className={styles.divLabel}>
              This doctor is not available on the selected date.
              <br />
              Would you like to check their next available slot?
            </div>
          )}
          <Button
            size="large"
            text="Find Next Availability"
            className={styles.button}
            onClick={() => {
              if (onSelectNextAvail) {
                onSelectNextAvail(availability);
              }
            }}
          />
        </div>
      </>
    );
  }

  const morningSlots: Slot[] = [];
  const eveningSlots: Slot[] = [];
  const afternoonSlots: Slot[] = [];
  if (
    timeSlotsForDoctor[searchDate] &&
    timeSlotsForDoctor[searchDate].length > 0
  ) {
    timeSlotsForDoctor[searchDate].map((slot) => {
      const timeInt = parseInt(parseStartTime(slot).format("h"));
      const amORpm = parseStartTime(slot).format("A");

      if (amORpm === "AM" && timeInt <= 12) {
        morningSlots.push(slot);
      } else if (amORpm === "PM" && (timeInt === 12 || timeInt < 4)) {
        afternoonSlots.push(slot);
      } else if (amORpm === "PM" && timeInt >= 4) {
        eveningSlots.push(slot);
      }
    });
  }

  /*
  const newSlots = {
    "morning" : [{"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"09:00:00","timeZoneName":"America/New_York"},
    {"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"10:00:00","timeZoneName":"America/New_York"},
    {"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"11:00:00","timeZoneName":"America/New_York"}],
    "afternoon" : [{"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"12:00:00","timeZoneName":"America/New_York"},
    {"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"1:00:00","timeZoneName":"America/New_York"},
    {"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"2:00:00","timeZoneName":"America/New_York"}],
    "evening" : [{"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"04:00:00","timeZoneName":"America/New_York"},
    {"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"05:00:00","timeZoneName":"America/New_York"},
    {"date":"2023-01-16","doctorId":"21100","endTime":"09:05:00","startTime":"06:00:00","timeZoneName":"America/New_York"}]
}*/

  return (
    <>
      {morningSlots && morningSlots.length > 0 && (
        <div
          className={
            isV3 && !useClassicStartLayout ? stylesV3.timeGroup : undefined
          }
        >
          <div className={styles.timeIcon}>
            <img src={morningIcon} alt="Morning" />
          </div>
          <div className={styles.timeSlotsContainer}>
            {morningSlots.map((slot) => (
              <SlotButton
                key={`${slot.date} ${slot.startTime}`}
                slot={slot}
                size="small"
                onSelectSlot={noop}
                version={2}
                onSelection={onSelection}
                selectedSlot={selectedSlot}
              />
            ))}
          </div>
        </div>
      )}
      {afternoonSlots && afternoonSlots.length > 0 && (
        <div
          className={
            isV3 && !useClassicStartLayout ? stylesV3.timeGroup : undefined
          }
        >
          <div className={styles.timeIcon}>
            <img src={afternoonIcon} alt="Afternoon" />
          </div>
          <div className={styles.timeSlotsContainer}>
            {afternoonSlots.map((slot) => (
              <SlotButton
                key={`${slot?.date} ${slot?.startTime}`}
                slot={slot}
                size="small"
                onSelectSlot={noop}
                version={2}
                onSelection={onSelection}
                selectedSlot={selectedSlot}
              />
            ))}
          </div>
        </div>
      )}
      {eveningSlots && eveningSlots.length > 0 && (
        <div
          className={
            isV3 && !useClassicStartLayout ? stylesV3.timeGroup : undefined
          }
        >
          <div className={styles.timeIcon}>
            <img
              src={eveningIcon}
              alt="Evening"
              className={styles.eveningIcon}
            />
          </div>
          <div className={styles.timeSlotsContainer}>
            {eveningSlots.map((slot) => (
              <SlotButton
                key={`${slot.date} ${slot.startTime}`}
                slot={slot}
                size="small"
                onSelectSlot={noop}
                version={2}
                onSelection={onSelection}
                selectedSlot={selectedSlot}
              />
            ))}
          </div>
        </div>
      )}
      {isMobileView && (
        <div
          ref={nextButtonRef}
          style={
            isV3 && !useClassicStartLayout
              ? { scrollMarginBottom: "28px" }
              : undefined
          }
        >
          <ButtonV2
            className={styles.scheduleExam}
            text="Next"
            onClick={onNextMobile ? onNextMobile : noop}
            role={ButtonRole.Standard}
            size={"large"}
            disabled={!(selectedSlot?.doctorId === doctorId)}
          />
        </div>
      )}
    </>
  );
};
